/**

 * CinematicHomeHero — premium 3-slide homepage hero with cosmic legacy artwork.

 */

import React, { useCallback, useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import {
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Flame,
  Lock,
  Menu,
  Mic,
} from 'lucide-react';

import { BrandWordmark } from '../brand/BrandWordmark';

import { PUBLIC_DEMO_ROUTE } from '../../utils/publicNavigation';

import {

  Carousel,

  CarouselContent,

  CarouselItem,

} from '../ui/carousel';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';

import cosmicHero from '../../assets/home/cosmic-legacy-hero.png';

import familyMemoryHero from '../../assets/home/family-memory-hero.jpg';

import '../../styles/home-hero.css';



const NAV = {
  features: 'Features',
  how: 'How it works',
  pricing: 'Pricing',
  faq: 'FAQ',
  help: 'Help & Guides',
  start: 'Start your archive',
  signIn: 'Sign in',
  contact: 'Contact',
  dashboard: 'Dashboard',
};



const SLIDES = [
  { id: 'legacy', label: 'Legacy hero' },
  { id: 'moments', label: 'Family moments' },
  { id: 'pillars', label: 'Product pillars' },
];

const HERO_PILLARS = [
  {
    title: 'AI Story Studio',
    copy: 'Turn memories into stories and Legacy Books.',
    to: '/story-studio',
    icon: BookOpen,
  },
  {
    title: 'Family Handover Vault',
    copy: 'Keep important family information private.',
    to: '/vault',
    icon: Lock,
  },
  {
    title: 'Memorial Bridge',
    copy: 'Create a tribute when the time is right.',
    to: '/memorials',
    icon: Flame,
  },
];



export function CinematicHomeHero({ onPrimaryClick, user, showcaseMode }) {

  const [api, setApi] = useState(null);

  const [current, setCurrent] = useState(0);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isSignedIn = Boolean(user || showcaseMode);

  const closeMobileNav = () => setMobileNavOpen(false);

  const mobileNavLinkClass =
    'rounded-lg px-3 py-2.5 text-base font-medium text-foreground transition hover:bg-muted';



  const onSelect = useCallback(() => {

    if (!api) return;

    setCurrent(api.selectedScrollSnap());

  }, [api]);



  useEffect(() => {

    if (!api) return undefined;

    onSelect();

    api.on('select', onSelect);

    api.on('reInit', onSelect);

    return () => {

      api.off('select', onSelect);

      api.off('reInit', onSelect);

    };

  }, [api, onSelect]);



  const scrollTo = useCallback((index) => {

    api?.scrollTo(index);

  }, [api]);



  return (

    <section

      className="cinematic-home-hero"

      aria-label="MyLegacyBridge homepage hero"

      data-testid="cinematic-home-hero"

    >

      <header className="cinematic-home-hero__nav" data-testid="landing-nav">

        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3.5 sm:px-6 lg:px-8">

          <Link

            to="/"

            className="flex min-w-0 shrink-0 items-center gap-2.5 rounded-full py-1 transition hover:bg-white/[0.04] sm:gap-3"

            data-testid="logo-link"

          >

            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#c9a961]/20 text-[#faf7f2] ring-1 ring-[#c9a961]/25 sm:h-10 sm:w-10">

              <span className="font-serif text-base leading-none sm:text-lg">G</span>

            </div>

            <BrandWordmark variant="navLanding" className="hidden truncate sm:block" />

          </Link>



          <nav className="hidden items-center gap-1 md:flex" aria-label="Page sections">

            <a
              href="#pillars"
              className="rounded-full px-3 py-1.5 text-sm font-medium text-[#e8e0d5]/80 transition hover:bg-white/[0.06] hover:text-[#faf7f2]"
            >
              {NAV.features}
            </a>
            <Link
              to={PUBLIC_DEMO_ROUTE}
              className="rounded-full px-3 py-1.5 text-sm font-medium text-[#e8e0d5]/80 transition hover:bg-white/[0.06] hover:text-[#faf7f2]"
            >
              {NAV.how}
            </Link>
            <Link to="/pricing" className="rounded-full px-3 py-1.5 text-sm font-medium text-[#e8e0d5]/80 transition hover:bg-white/[0.06] hover:text-[#faf7f2]">

              {NAV.pricing}

            </Link>

            <Link to="/faq" className="rounded-full px-3 py-1.5 text-sm font-medium text-[#e8e0d5]/80 transition hover:bg-white/[0.06] hover:text-[#faf7f2]">

              {NAV.faq}

            </Link>

            <Link to="/help" className="rounded-full px-3 py-1.5 text-sm font-medium text-[#e8e0d5]/80 transition hover:bg-white/[0.06] hover:text-[#faf7f2]" data-testid="landing-nav-help">

              {NAV.help}

            </Link>

          </nav>



          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>

            <SheetTrigger asChild>

              <button

                type="button"

                className="rounded-full p-2 text-[#e8e0d5]/85 transition hover:bg-white/[0.06] hover:text-[#faf7f2] md:hidden"

                aria-label="Open menu"

                data-testid="landing-mobile-menu-btn"

              >

                <Menu className="h-5 w-5" />

              </button>

            </SheetTrigger>

            <SheetContent side="right" className="w-[min(18rem,85vw)]" data-testid="landing-mobile-menu">

              <SheetHeader>

                <SheetTitle className="font-serif text-left">Menu</SheetTitle>

              </SheetHeader>

              <nav className="mt-6 flex flex-col gap-1" aria-label="Page sections">

                <a href="#pillars" className={mobileNavLinkClass} onClick={closeMobileNav}>

                  {NAV.features}

                </a>

                <Link to={PUBLIC_DEMO_ROUTE} className={mobileNavLinkClass} onClick={closeMobileNav}>

                  {NAV.how}

                </Link>

                <Link to="/pricing" className={mobileNavLinkClass} onClick={closeMobileNav}>

                  {NAV.pricing}

                </Link>

                <Link to="/faq" className={mobileNavLinkClass} onClick={closeMobileNav}>

                  {NAV.faq}

                </Link>

                <Link to="/help" className={mobileNavLinkClass} onClick={closeMobileNav} data-testid="landing-mobile-nav-help">

                  {NAV.help}

                </Link>

              </nav>

            </SheetContent>

          </Sheet>



          <div className="flex shrink-0 items-center justify-end gap-2 sm:gap-2.5">

            {isSignedIn ? (

              <>

                <button

                  type="button"

                  onClick={onPrimaryClick}

                  className="rounded-full px-2.5 py-2 text-xs font-medium text-[#e8e0d5]/85 transition hover:text-[#faf7f2] sm:px-3 sm:text-sm"

                >

                  <span className="sm:hidden">Start</span>

                  <span className="hidden sm:inline">{NAV.start}</span>

                </button>

                <Link

                  to="/dashboard"

                  className="inline-flex items-center justify-center rounded-full border border-[#c9a961]/30 bg-[#c9a961]/10 px-3 py-2 text-xs font-medium text-[#faf7f2] transition hover:bg-[#c9a961]/18 sm:px-3.5 sm:text-sm"

                >

                  {NAV.dashboard}

                </Link>

              </>

            ) : (

              <>

                <button

                  type="button"

                  onClick={onPrimaryClick}

                  className="hidden rounded-full border border-white/[0.08] bg-white/[0.06] px-3 py-2 text-sm font-medium text-[#faf7f2] transition hover:bg-white/[0.1] sm:inline-flex"

                >

                  {NAV.start}

                </button>

                <Link to="/login" className="rounded-full px-3 py-2 text-sm font-medium text-[#e8e0d5]/85 transition hover:text-[#faf7f2]">

                  {NAV.signIn}

                </Link>

                <Link

                  to="/contact"

                  className="inline-flex items-center justify-center rounded-full border border-[#c9a961]/30 bg-[#c9a961]/10 px-3.5 py-2 text-sm font-medium text-[#faf7f2] transition hover:bg-[#c9a961]/18"

                >

                  {NAV.contact}

                </Link>

              </>

            )}

          </div>

        </div>

      </header>



      <div className="cinematic-home-hero__carousel-wrap">

        <Carousel

          setApi={setApi}

          opts={{ loop: true, align: 'start', duration: 28 }}

          className="cinematic-home-hero__carousel w-full"

          aria-roledescription="carousel"

          aria-label="Product story slides"

        >

          <CarouselContent className="-ml-0 h-full">

            {/* Slide 1 — Cosmic legacy */}

            <CarouselItem className="basis-full pl-0">

              <article className="cinematic-home-hero__slide" aria-labelledby="hero-slide-legacy-heading">

                <div className="cinematic-home-hero__slide-bg cinematic-home-hero__slide-bg--legacy" aria-hidden />

                <div className="cinematic-home-hero__slide-overlay" aria-hidden />

                <div className="cinematic-home-hero__slide-inner">

                  <div className="cinematic-home-hero__copy">

                    <h1 id="hero-slide-legacy-heading" className="cinematic-home-hero__headline">

                      From first memories to lasting legacy.

                    </h1>

                    <p className="cinematic-home-hero__subcopy">

                      Preserve stories, voice memories, keepsakes, memorials and Legacy Books in one private family archive.

                    </p>

                    <div className="cinematic-home-hero__actions">

                      <button type="button" className="cinematic-home-hero__btn-primary" onClick={onPrimaryClick} data-testid="hero-cta-btn">

                        Start your archive

                        <ArrowRight className="h-4 w-4 opacity-80" />

                      </button>

                      <Link to={PUBLIC_DEMO_ROUTE} className="cinematic-home-hero__btn-secondary" data-testid="hero-demo-cta-btn">

                        View demo

                        <BookOpen className="h-4 w-4 opacity-85" />

                      </Link>

                      <a href="#pillars" className="cinematic-home-hero__btn-tertiary" data-testid="hero-secondary-cta-btn">

                        Explore features

                      </a>

                    </div>

                  </div>

                  <div className="cinematic-home-hero__visual cinematic-home-hero__visual--cosmic">

                    <img

                      src={cosmicHero}

                      alt="A luminous profile symbolising memory, identity and legacy."

                      className="cinematic-home-hero__cosmic-img"

                      fetchPriority="high"

                      decoding="async"

                    />

                  </div>

                </div>

              </article>

            </CarouselItem>



            {/* Slide 2 — Genuine family moment */}

            <CarouselItem className="basis-full pl-0">

              <article className="cinematic-home-hero__slide" aria-labelledby="hero-slide-moments-heading">

                <div className="cinematic-home-hero__slide-bg cinematic-home-hero__slide-bg--story" aria-hidden />

                <div className="cinematic-home-hero__slide-overlay cinematic-home-hero__slide-overlay--warm" aria-hidden />

                <div className="cinematic-home-hero__slide-inner">

                  <div className="cinematic-home-hero__copy">

                    <p className="cinematic-home-hero__eyebrow">First memories</p>

                    <h2 id="hero-slide-moments-heading" className="cinematic-home-hero__headline">

                      Preserve the moments that still feel alive.

                    </h2>

                    <p className="cinematic-home-hero__subcopy">

                      A family archive can begin with one photo, one voice note, or one object that mattered.

                    </p>

                    <div className="cinematic-home-hero__actions">

                      <button type="button" className="cinematic-home-hero__btn-primary" onClick={onPrimaryClick}>

                        Capture a memory

                        <Mic className="h-4 w-4 opacity-80" />

                      </button>

                      <Link to="/story-studio" className="cinematic-home-hero__btn-secondary">
                        Open Story Studio
                        <BookOpen className="h-4 w-4 opacity-85" />
                      </Link>

                    </div>

                  </div>

                  <div className="cinematic-home-hero__visual cinematic-home-hero__visual--photo">

                    <img

                      src={familyMemoryHero}

                      alt="Two children with a soft blue toy — a genuine family moment worth preserving."

                      className="cinematic-home-hero__family-img"

                      loading="lazy"

                      decoding="async"

                    />

                  </div>

                </div>

              </article>

            </CarouselItem>



            {/* Slide 3 — Three product pillars */}

            <CarouselItem className="basis-full pl-0">

              <article className="cinematic-home-hero__slide" aria-labelledby="hero-slide-pillars-heading">

                <div className="cinematic-home-hero__slide-bg cinematic-home-hero__slide-bg--output" aria-hidden />

                <div className="cinematic-home-hero__slide-overlay" aria-hidden />

                <div className="cinematic-home-hero__slide-inner cinematic-home-hero__slide-inner--pillars">

                  <div className="cinematic-home-hero__copy cinematic-home-hero__copy--pillars">

                    <h2
                      id="hero-slide-pillars-heading"
                      className="cinematic-home-hero__headline cinematic-home-hero__headline--pillars"
                    >

                      Capture a life. Organise what matters. Preserve it across generations.

                    </h2>

                    <p className="cinematic-home-hero__subcopy cinematic-home-hero__subcopy--pillars">

                      Three connected ways to capture stories, organise what matters, and preserve a life with care.

                    </p>

                    <div className="cinematic-home-hero__actions">

                      <a href="#pillars" className="cinematic-home-hero__btn-primary">

                        Explore features

                        <ArrowRight className="h-4 w-4 opacity-80" />

                      </a>

                      <Link to="/pricing" className="cinematic-home-hero__btn-secondary">

                        View pricing

                        <BookOpen className="h-4 w-4 opacity-85" />

                      </Link>

                    </div>

                  </div>

                  <nav className="cinematic-home-hero__visual cinematic-home-hero__visual--pillars" aria-label="Product pillars">

                    <div className="cinematic-home-hero__pillar-stack">

                      {HERO_PILLARS.map((pillar) => {

                        const Icon = pillar.icon;

                        return (

                          <Link

                            key={pillar.title}

                            to={pillar.to}

                            className="cinematic-home-hero__pillar-card"

                            data-testid={`hero-pillar-${pillar.title.toLowerCase().replace(/\s+/g, '-')}`}

                          >

                            <span className="cinematic-home-hero__pillar-card-icon" aria-hidden>

                              <Icon className="h-4 w-4" strokeWidth={1.75} />

                            </span>

                            <span className="cinematic-home-hero__pillar-card-body">

                              <span className="cinematic-home-hero__pillar-label">{pillar.title}</span>

                              <span className="cinematic-home-hero__pillar-copy">{pillar.copy}</span>

                            </span>

                            <span className="cinematic-home-hero__pillar-open">

                              Open

                              <ArrowRight className="h-3.5 w-3.5" aria-hidden />

                            </span>

                          </Link>

                        );

                      })}

                    </div>

                  </nav>

                </div>

              </article>

            </CarouselItem>

          </CarouselContent>

        </Carousel>



        <div className="cinematic-home-hero__controls" role="group" aria-label="Hero slide controls">

          <button

            type="button"

            className="cinematic-home-hero__control-btn"

            onClick={() => api?.scrollPrev()}

            disabled={!api}

            aria-label="Previous slide"

          >

            <ChevronLeft className="h-4 w-4" />

          </button>

          <div className="cinematic-home-hero__dots" role="tablist" aria-label="Choose slide">

            {SLIDES.map((slide, index) => (

              <button

                key={slide.id}

                type="button"

                role="tab"

                aria-selected={current === index}

                aria-label={slide.label}

                className={`cinematic-home-hero__dot ${current === index ? 'cinematic-home-hero__dot--active' : ''}`}

                onClick={() => scrollTo(index)}

              />

            ))}

          </div>

          <button

            type="button"

            className="cinematic-home-hero__control-btn"

            onClick={() => api?.scrollNext()}

            disabled={!api}

            aria-label="Next slide"

          >

            <ChevronRight className="h-4 w-4" />

          </button>

        </div>

      </div>

    </section>

  );

}



export default CinematicHomeHero;


